import writeXlsxFile from 'write-excel-file/browser';
import { downloadZip } from 'client-zip';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

export interface TableElement {
  [key: string]: string | number;
}

export interface ExportTotalizador {
  label: string;
  value: string | number;
}

export const MAX_EXPORT_TOTALIZADORES = 6;

/** Um arquivo montado e ainda não salvo — o nome já tem data, hora e extensão. */
export interface ArquivoExportado {
  nome: string;
  blob: Blob;
}

export interface ColumnDefinition<T> {
  key: keyof T;
  header: string;
}

export enum ExportType {
  Excel = 'excel',
  Ods = 'ods',
  Csv = 'csv',
  Pdf = 'pdf',
  Json = 'json',
  Clipboard = 'clipboard',
  Print = 'print',
  PrintPdf = 'printpdf',
  Xml = 'xml'
}

export const EXPORT_TYPE_MESSAGE: Record<ExportType, string> = {
  [ExportType.Excel]: 'Exportando para Excel...',
  [ExportType.Ods]: 'Exportando para ODS...',
  [ExportType.Csv]: 'Exportando para CSV...',
  [ExportType.Pdf]: 'Gerando PDF...',
  [ExportType.Json]: 'Gerando JSON...',
  [ExportType.Clipboard]: 'Copiando para a área de transferência...',
  [ExportType.Print]: 'Preparando impressão...',
  [ExportType.PrintPdf]: 'Preparando impressão...',
  [ExportType.Xml]: 'Gerando XML...'
};

const TITULO_TOTALIZADORES = 'Totalizadores';
const COR_DESTAQUE_PDF: [number, number, number] = [16, 155, 242];
const LARGURA_MINIMA_COLUNA = 8;
const LARGURA_MAXIMA_COLUNA = 60;

type CelulaExportacao = string | number;

interface LinhaExportacao {
  celulas: CelulaExportacao[];
  destaque: boolean;
}

export class TableExportUtil {
  static validate(data: Partial<TableElement>[]) {
    if (!data || data.length === 0) {
      throw new Error('Exportação cancelada: dados vazios');
    }
  }

  static getSheetName(name: string) {
    const sheetName = (name || 'ExportResult')
      .replace(/[:\\/?*[\]]/g, '')
      .trim();

    return (sheetName || 'ExportResult').slice(0, 31);
  }

  static getFileName(name: string) {
    const agora = new Date();
    const doisDigitos = (valor: number) => String(valor).padStart(2, '0');

    const data = `${doisDigitos(agora.getDate())}-${doisDigitos(agora.getMonth() + 1)}-${agora.getFullYear()}`;
    const hora = `${doisDigitos(agora.getHours())}h${doisDigitos(agora.getMinutes())}`;

    return `${name} - ${data} ${hora}`;
  }

  static limitarTotalizadores(totalizadores?: ExportTotalizador[]): ExportTotalizador[] {
    const validos = (totalizadores ?? []).filter(totalizador => !!totalizador?.label);

    if (validos.length > MAX_EXPORT_TOTALIZADORES) {
      console.warn(`Exportação aceita até ${MAX_EXPORT_TOTALIZADORES} totalizadores; os demais foram ignorados.`);
    }

    return validos.slice(0, MAX_EXPORT_TOTALIZADORES);
  }

  private static getKeys(arr: Partial<TableElement>[]) {
    return Object.keys(arr[0] ?? {});
  }

  private static downloadBlob(blob: Blob, fileName: string) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);
  }

  private static escapeXml(value: unknown): string {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&apos;');
  }

  private static montarLinhas(arr: Partial<TableElement>[], totalizadores?: ExportTotalizador[]): LinhaExportacao[] {
    const keys = this.getKeys(arr);
    const totais = this.limitarTotalizadores(totalizadores);

    const linhasTotais: LinhaExportacao[] = totais.length
      ? [
        { celulas: [TITULO_TOTALIZADORES], destaque: true },
        ...totais.map(totalizador => ({ celulas: [totalizador.label, totalizador.value], destaque: false })),
        { celulas: [], destaque: false }
      ]
      : [];

    return [
      ...linhasTotais,
      { celulas: keys, destaque: true },
      ...arr.map(row => ({ celulas: keys.map(key => (row as any)[key] ?? ''), destaque: false }))
    ];
  }

  private static calcularLarguraColunas(linhas: LinhaExportacao[]) {
    const larguras: number[] = [];

    for (const linha of linhas) {
      linha.celulas.forEach((valor, coluna) => {
        larguras[coluna] = Math.max(larguras[coluna] ?? 0, String(valor ?? '').length);
      });
    }

    return Array.from(larguras, largura => ({
      width: Math.min(LARGURA_MAXIMA_COLUNA, Math.max(LARGURA_MINIMA_COLUNA, (largura ?? 0) + 2))
    }));
  }

  private static buildDelimitedContent(linhas: LinhaExportacao[], separator: string) {
    const escapeCell = (value: unknown) => {
      const text = String(value ?? '').replace(/"/g, '""');
      return /["\r\n;]/.test(text) ? `"${text}"` : text;
    };

    return linhas
      .map(linha => linha.celulas.map(escapeCell).join(separator))
      .join('\r\n');
  }

  /**
   * Monta o arquivo de um formato SEM salvar — para quem precisa juntar vários
   * (o ZIP do "Exportar todos" das Métricas de Atendimento). Os `exportTo*`
   * chamam os mesmos montadores e salvam, então o arquivo é o mesmo.
   * Impressão e área de transferência não geram arquivo.
   */
  static async gerarArquivo(type: ExportType, arr: Partial<TableElement>[], name: string, totalizadores?: ExportTotalizador[]): Promise<ArquivoExportado> {
    switch (type) {
      case ExportType.Excel:
        return this.montarExcel(arr, name, totalizadores);
      case ExportType.Ods:
        return this.montarOds(arr, name, totalizadores);
      case ExportType.Csv:
        return this.montarCsv(arr, name, totalizadores);
      case ExportType.Pdf:
        return { nome: `${this.getFileName(name)}.pdf`, blob: this.generatePdfBlob(arr, name, totalizadores) };
      case ExportType.Json:
        return this.montarJson(arr, name, totalizadores);
      case ExportType.Xml:
        return this.montarXml(arr, name, totalizadores);
      default:
        throw new Error(`O formato ${type} não gera arquivo`);
    }
  }

  /** Salva um arquivo montado por {@link gerarArquivo}. */
  static salvar(arquivo: ArquivoExportado) {
    this.downloadBlob(arquivo.blob, arquivo.nome);
  }

  /** Junta os arquivos num ZIP e salva com o nome informado (data e hora são acrescentadas). */
  static async salvarZip(arquivos: ArquivoExportado[], name: string) {
    const response = downloadZip(arquivos.map(arquivo => ({ name: arquivo.nome, input: arquivo.blob })));

    this.downloadBlob(await response.blob(), `${this.getFileName(name)}.zip`);
  }

  static async exportToExcel(arr: Partial<TableElement>[], name: string, totalizadores?: ExportTotalizador[]) {
    this.salvar(await this.montarExcel(arr, name, totalizadores));
  }

  private static async montarExcel(arr: Partial<TableElement>[], name: string, totalizadores?: ExportTotalizador[]): Promise<ArquivoExportado> {
    this.validate(arr);

    const fileName = this.getFileName(name);
    const linhas = this.montarLinhas(arr, totalizadores);
    const data = linhas.map(linha => linha.celulas.map(value => (
      linha.destaque ? { value, fontWeight: 'bold' as const } : { value }
    )));

    const blob = await writeXlsxFile(data, {
      sheet: this.getSheetName(name),
      columns: this.calcularLarguraColunas(linhas)
    }).toBlob();

    return { nome: `${fileName}.xlsx`, blob };
  }

  static async exportToODS(arr: Partial<TableElement>[], name: string, totalizadores?: ExportTotalizador[]) {
    this.salvar(await this.montarOds(arr, name, totalizadores));
  }

  private static async montarOds(arr: Partial<TableElement>[], name: string, totalizadores?: ExportTotalizador[]): Promise<ArquivoExportado> {
    this.validate(arr);

    const fileName = this.getFileName(name);
    const sheetName = this.getSheetName(name);
    const tableRows = this.montarLinhas(arr, totalizadores).map(linha => {
      const cells = linha.celulas.map(value => (
        `<table:table-cell office:value-type="string"><text:p>${this.escapeXml(value)}</text:p></table:table-cell>`
      )).join('');

      return `<table:table-row>${cells || '<table:table-cell/>'}</table:table-row>`;
    }).join('');

    const contentXml = `<?xml version="1.0" encoding="UTF-8"?>
<office:document-content
  xmlns:office="urn:oasis:names:tc:opendocument:xmlns:office:1.0"
  xmlns:table="urn:oasis:names:tc:opendocument:xmlns:table:1.0"
  xmlns:text="urn:oasis:names:tc:opendocument:xmlns:text:1.0"
  office:version="1.2">
  <office:body>
    <office:spreadsheet>
      <table:table table:name="${this.escapeXml(sheetName)}">
        ${tableRows}
      </table:table>
    </office:spreadsheet>
  </office:body>
</office:document-content>`;

    const manifestXml = `<?xml version="1.0" encoding="UTF-8"?>
<manifest:manifest xmlns:manifest="urn:oasis:names:tc:opendocument:xmlns:manifest:1.0" manifest:version="1.2">
  <manifest:file-entry manifest:full-path="/" manifest:media-type="application/vnd.oasis.opendocument.spreadsheet"/>
  <manifest:file-entry manifest:full-path="content.xml" manifest:media-type="text/xml"/>
</manifest:manifest>`;

    const response = downloadZip([
      { name: 'mimetype', input: 'application/vnd.oasis.opendocument.spreadsheet' },
      { name: 'content.xml', input: contentXml },
      { name: 'META-INF/' },
      { name: 'META-INF/manifest.xml', input: manifestXml }
    ]);

    return { nome: `${fileName}.ods`, blob: await response.blob() };
  }

  static exportToCSV(arr: Partial<TableElement>[], name: string, totalizadores?: ExportTotalizador[]) {
    this.salvar(this.montarCsv(arr, name, totalizadores));
  }

  private static montarCsv(arr: Partial<TableElement>[], name: string, totalizadores?: ExportTotalizador[]): ArquivoExportado {
    this.validate(arr);

    const fileName = this.getFileName(name);
    const csv = this.buildDelimitedContent(this.montarLinhas(arr, totalizadores), ';');
    const BOM = '﻿';
    const blob = new Blob([BOM + csv], {
      type: 'text/csv;charset=utf-8;'
    });

    return { nome: `${fileName}.csv`, blob };
  }

  private static drawFileIcon(doc: jsPDF, x: number, y: number, tamanho: number): void {
    const largura = tamanho * 0.78;
    const dobra = largura * 0.4;

    doc.setDrawColor(255, 255, 255);
    doc.setLineWidth(0.4);

    doc.lines(
      [
        [largura - dobra, 0],
        [dobra, dobra],
        [0, tamanho - dobra],
        [-largura, 0],
        [0, -tamanho]
      ],
      x, y, [1, 1], 'S', false
    );

    const linhaX1 = x + largura * 0.18;
    const linhaX2 = x + largura * 0.82;
    doc.line(linhaX1, y + tamanho * 0.5, linhaX2, y + tamanho * 0.5);
    doc.line(linhaX1, y + tamanho * 0.68, linhaX2 - largura * 0.2, y + tamanho * 0.68);
  }

  private static drawTotalizadores(doc: jsPDF, startY: number, totalizadores?: ExportTotalizador[]): number {
    const totais = this.limitarTotalizadores(totalizadores);

    if (!totais.length) {
      return startY;
    }

    const margem = 10;
    const espaco = 4;
    const altura = 20;
    const padding = 4;
    const larguraUtil = doc.internal.pageSize.getWidth() - margem * 2;
    const largura = (larguraUtil - espaco * (totais.length - 1)) / totais.length;
    const larguraTexto = largura - padding * 2;

    const ajustarFonte = (texto: string, tamanhoInicial: number, tamanhoMinimo: number) => {
      let tamanho = tamanhoInicial;
      doc.setFontSize(tamanho);

      while (tamanho > tamanhoMinimo && doc.getTextWidth(texto) > larguraTexto) {
        tamanho -= 0.5;
        doc.setFontSize(tamanho);
      }
    };

    totais.forEach((totalizador, index) => {
      const x = margem + index * (largura + espaco);

      doc.setFillColor(245, 247, 250);
      doc.setDrawColor(220, 220, 220);
      doc.setLineWidth(0.1);
      doc.roundedRect(x, startY, largura, altura, 1.5, 1.5, 'FD');

      doc.setFillColor(...COR_DESTAQUE_PDF);
      doc.rect(x, startY, 1.2, altura, 'F');

      const label = totalizador.label.toUpperCase();
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(110);
      ajustarFonte(label, 8, 5);
      doc.text(label, x + padding, startY + 7);

      const valor = String(totalizador.value);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(40);
      ajustarFonte(valor, 15, 7);
      doc.text(valor, x + padding, startY + 15.5);
    });

    return startY + altura + 5;
  }

  private static montarPdf(arr: Partial<TableElement>[], name: string, totalizadores?: ExportTotalizador[]): jsPDF {
    this.validate(arr);

    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4'
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    const headerHeight = 22;
    const iconeTamanho = 8;
    const iconeX = 14;
    const iconeY = (headerHeight - iconeTamanho) / 2 - 2;

    doc.setFillColor(...COR_DESTAQUE_PDF);
    doc.rect(0, 0, pageWidth, headerHeight, 'F');

    this.drawFileIcon(doc, iconeX, iconeY, iconeTamanho);

    doc.setTextColor(255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.text(name, iconeX + iconeTamanho + 4, 14);

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text(
      `Gerado em: ${new Date().toLocaleDateString('pt-BR')} ${new Date().toLocaleTimeString('pt-BR')}`,
      pageWidth - 14,
      14,
      { align: 'right' }
    );
    doc.text('Sistemas Imap', pageWidth - 14, 19, { align: 'right' });

    const keys = this.getKeys(arr);

    const body = arr.map(row =>
      keys.map(k => String((row as any)[k] ?? ''))
    );

    autoTable(doc, {
      head: [keys],
      body,
      startY: this.drawTotalizadores(doc, headerHeight + 5, totalizadores),
      margin: { left: 10, right: 10 },
      styles: {
        fontSize: 9,
        cellPadding: 3,
        overflow: 'linebreak',
        valign: 'middle'
      },
      headStyles: {
        fillColor: [230, 240, 255],
        textColor: 40,
        fontStyle: 'bold',
        halign: 'left'
      },
      bodyStyles: {
        textColor: 50
      },
      alternateRowStyles: {
        fillColor: [245, 247, 250]
      },
      tableLineColor: [220, 220, 220],
      tableLineWidth: 0.1
    });

    const pageCount = doc.getNumberOfPages();

    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);

      doc.setDrawColor(220);
      doc.line(10, pageHeight - 15, pageWidth - 10, pageHeight - 15);

      doc.setFontSize(9);
      doc.setTextColor(120);
      doc.text(
        `Página ${i} de ${pageCount}`,
        pageWidth - 10,
        pageHeight - 8,
        { align: 'right' }
      );
    }

    return doc;
  }

  static exportToPDF(arr: Partial<TableElement>[], name: string, totalizadores?: ExportTotalizador[]) {
    this.montarPdf(arr, name, totalizadores).save(`${this.getFileName(name)}.pdf`);
  }

  static exportToJSON(arr: Partial<TableElement>[], name: string, totalizadores?: ExportTotalizador[]) {
    this.salvar(this.montarJson(arr, name, totalizadores));
  }

  private static montarJson(arr: Partial<TableElement>[], name: string, totalizadores?: ExportTotalizador[]): ArquivoExportado {
    this.validate(arr);

    const fileName = this.getFileName(name);
    const totais = this.limitarTotalizadores(totalizadores);
    const json = JSON.stringify(totais.length ? { totalizadores: totais, dados: arr } : arr, null, 2);

    const blob = new Blob([json], {
      type: 'application/json;charset=utf-8;'
    });

    return { nome: `${fileName}.json`, blob };
  }

  static async exportToClipboard(arr: Partial<TableElement>[], totalizadores?: ExportTotalizador[]) {
    this.validate(arr);

    const tsv = this.montarLinhas(arr, totalizadores)
      .map(linha => linha.celulas
        .map(value => String(value ?? '').replace(/\t/g, ' ').replace(/\n/g, ' '))
        .join('\t'))
      .join('\n');

    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(tsv);
    } else {
      const textarea = document.createElement('textarea');
      textarea.value = tsv;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
    }
  }

  static generatePdfBlob(arr: Partial<TableElement>[], name: string, totalizadores?: ExportTotalizador[]): Blob {
    return this.montarPdf(arr, name, totalizadores).output('blob');
  }

  static exportToPrintPdf(arr: Partial<TableElement>[], name: string, totalizadores?: ExportTotalizador[]) {
    const blob = this.generatePdfBlob(arr, name, totalizadores);
    const url = URL.createObjectURL(blob);

    const printWindow = window.open(url);

    if (!printWindow) {
      throw new Error('Não foi possível abrir a janela de impressão');
    }

    printWindow.onload = () => {
      printWindow.focus();
      printWindow.print();
    };
  }

  static exportToPrint() {
    setTimeout(() => {
      window.print();
    }, 800);
  }

  static exportToXml(arr: Partial<TableElement>[], name: string, totalizadores?: ExportTotalizador[]) {
    this.salvar(this.montarXml(arr, name, totalizadores));
  }

  private static montarXml(arr: Partial<TableElement>[], name: string, totalizadores?: ExportTotalizador[]): ArquivoExportado {
    this.validate(arr);

    const fileName = this.getFileName(name);
    const rootName = 'items';
    const itemName = 'item';

    const sanitizeTagName = (key: string): string => {
      return key
        .replace(/\s+/g, '_')
        .replace(/[^a-zA-Z0-9_-]/g, '');
    };

    const totais = this.limitarTotalizadores(totalizadores);
    const totaisXml = totais.length
      ? [
        '  <totalizadores>',
        ...totais.map(totalizador =>
          `    <totalizador>\n      <descricao>${this.escapeXml(totalizador.label)}</descricao>\n      <valor>${this.escapeXml(totalizador.value)}</valor>\n    </totalizador>`
        ),
        '  </totalizadores>'
      ]
      : [];

    const xmlContent = [
      '<?xml version="1.0" encoding="UTF-8"?>',
      `<${rootName}>`,
      ...totaisXml,
      ...arr.map(row => {
        const fields = Object.entries(row)
          .map(([key, value]) => {
            const tagName = sanitizeTagName(key);
            return `    <${tagName}>${this.escapeXml(value)}</${tagName}>`;
          })
          .join('\n');

        return `  <${itemName}>\n${fields}\n  </${itemName}>`;
      }),
      `</${rootName}>`
    ].join('\n');

    const blob = new Blob([xmlContent], {
      type: 'application/xml;charset=utf-8;'
    });

    return { nome: `${fileName}.xml`, blob };
  }
}
